import type { IDepositBankOption, IDepositItemsParams } from "src/@types/deposit-reconciliation";

import { DEPOSIT_STATUS_FILTER } from "../../constants/status";
import type { DepositStatusFilter } from "../../constants/status";

import type {
  DepositFilterKey,
  DepositFilterValues,
  LocalFilters,
  NsuFilterKey,
  NsuFilterValues,
  OptionType,
} from "../types";

type DepositQueryParams = Pick<
  IDepositItemsParams,
  | "depositBankCode"
  | "depositAgency"
  | "depositAccount"
  | "depositDescription"
  | "depositAmount"
  | "depositDateStart"
  | "depositDateEnd"
  | "depositStatus"
>;

type NsuQueryParams = Pick<
  IDepositItemsParams,
  "nsuNumber" | "nsuOperator" | "nsuDateStart" | "nsuDateEnd" | "nsuAmount"
>;

function knownDepositStatus(value: string | undefined): DepositStatusFilter | undefined {
  return Object.values(DEPOSIT_STATUS_FILTER).find(status => status === value);
}

const DEPOSIT_FILTER_KEYS: readonly DepositFilterKey[] = [
  "depositBankCode",
  "depositAgency",
  "depositAccount",
  "depositDescription",
  "depositAmount",
  "depositDateStart",
  "depositDateEnd",
  "depositStatus",
];

const NSU_FILTER_KEYS: readonly NsuFilterKey[] = [
  "nsuNumber",
  "nsuOperator",
  "nsuDateStart",
  "nsuDateEnd",
  "nsuAmount",
];

function amountParam(typed: string | undefined): number | undefined {
  return typed ? Number(typed) : undefined;
}

function pickFilterValues<Key extends keyof LocalFilters>(
  filters: LocalFilters,
  keys: readonly Key[],
): Partial<Record<Key, string>> {
  const values: Partial<Record<Key, string>> = {};
  for (const key of keys) {
    const value: LocalFilters[Key] = filters[key];
    if (typeof value === "string") values[key] = value;
  }
  return values;
}

export function depositQueryParams(filters: LocalFilters): DepositQueryParams {
  return {
    depositBankCode: filters.depositBankCode,
    depositAgency: filters.depositAgency,
    depositAccount: filters.depositAccount,
    depositDescription: filters.depositDescription,
    depositAmount: amountParam(filters.depositAmount),
    depositDateStart: filters.depositDateStart,
    depositDateEnd: filters.depositDateEnd,
    depositStatus: knownDepositStatus(filters.depositStatus),
  };
}

export function bankFilterOptions(banks: readonly IDepositBankOption[] | undefined): OptionType[] {
  return (banks ?? []).map(bank => ({ value: bank.name, label: `${bank.name} (${bank.code})` }));
}

export function depositFilterValues(filters: LocalFilters): DepositFilterValues {
  return pickFilterValues(filters, DEPOSIT_FILTER_KEYS);
}

export function nsuQueryParams(filters: LocalFilters): NsuQueryParams {
  return {
    nsuNumber: filters.nsuNumber,
    nsuOperator: filters.nsuOperator,
    nsuDateStart: filters.nsuDateStart,
    nsuDateEnd: filters.nsuDateEnd,
    nsuAmount: amountParam(filters.nsuAmount),
  };
}

export function nsuFilterValues(filters: LocalFilters): NsuFilterValues {
  return pickFilterValues(filters, NSU_FILTER_KEYS);
}
